import { Input, Modal } from "antd";
import { useEffect, useState } from "react";

export type CategoryModalData = {
  type: "new-category" | "edit-category";
  currentName?: string;
};

interface CategoryModalProps {
  modalData: CategoryModalData | null;
  categoryName: string;
  onClose: () => void;
  onSubmit: (selectedColor: string) => void;
  onNameChange: (value: string) => void;
  initialColor?: string | null;
  existingCategoryColors?: string[];
}

// Colors based on the provided image palette
const PRESET_COLORS = [
  "#5FD3A6", // Teal/Mint Green
  "#FCD757", // Yellow
  "#FFA96D", // Orange
  "#FF7F7F", // Salmon/Coral Red
  "#A390E4", // Lavender/Light Purple
  "#6CAEFF", // Sky Blue
  "#70D1E1", // Light Blue/Aqua
  "#9CD35B", // Lime Green
  "#F578B5", // Pink
  "#8A94A8", // Grey/Slate
];

export const CategoryModal = ({
  modalData,
  categoryName,
  onClose,
  onSubmit,
  onNameChange,
  initialColor,
  existingCategoryColors = [],
}: CategoryModalProps) => {
  const [selectedColor, setSelectedColor] = useState(() => {
    if (initialColor && PRESET_COLORS.includes(initialColor)) {
      return initialColor;
    }
    return PRESET_COLORS[0];
  });

  useEffect(() => {
    if (modalData?.type === "new-category") {
      const availableColors = PRESET_COLORS.filter(
        (color) => !existingCategoryColors.includes(color)
      );

      if (availableColors.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableColors.length);
        setSelectedColor(availableColors[randomIndex]);
      } else {
        // Fallback if all colors are used
        const randomIndex = Math.floor(Math.random() * PRESET_COLORS.length);
        setSelectedColor(PRESET_COLORS[randomIndex]);
      }
    } else if (modalData?.type === "edit-category") {
      if (initialColor && PRESET_COLORS.includes(initialColor)) {
        setSelectedColor(initialColor);
      } else {
        setSelectedColor(PRESET_COLORS[0]);
      }
    }
  }, [modalData, initialColor, existingCategoryColors]);

  const handleOk = () => {
    onSubmit(selectedColor);
  };

  return (
    <Modal
      title={
        modalData?.type === "new-category" ? "New Category" : "Edit Category"
      }
      open={modalData !== null}
      onOk={handleOk}
      onCancel={onClose}
      okText={modalData?.type === "new-category" ? "Create" : "Save"}
      destroyOnClose
    >
      <div className="mt-4 space-y-4 mb-8">
        <Input
          placeholder="Enter category name"
          value={categoryName}
          onChange={(e) => onNameChange(e.target.value)}
          onPressEnter={handleOk}
          autoFocus
          maxLength={40}
        />
        <div>
          <label className="block mb-2 text-sm font-medium text-antd-colorText">
            Select Color
          </label>
          <div className="flex flex-wrap gap-3">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`w-8 h-8 rounded-full border-2 border-transparent transition-all duration-150 ${
                  selectedColor === color
                    ? "ring-2 ring-offset-1 ring-offset-white ring-antd-colorInfo scale-110"
                    : "hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-white focus:ring-antd-colorInfo"
                }`}
                style={{ backgroundColor: color }}
                onClick={() => setSelectedColor(color)}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
